import { DateOnly } from '@if/core/date'
import { EWorksheetStatus } from '@if/core/types'
import * as yup from '@if/core/yup'
import {
  BaseWorksheetFormConfig,
  BaseWorksheetFormValues,
  buildBaseWorksheetFormInitialValues,
  isPromoPlan,
  StoredTermAndPlan,
  useBaseWorksheetFormFields,
  useBaseWorksheetFormReady,
  WorksheetError,
} from '@src/components/WorksheetForm'
import { BaseWorksheetDto } from '@src/data/types/BaseWorksheetSchema'
import { CreditApplication } from '@src/data/types/CreditApplicationSchema'
import { Merchant } from '@src/data/types/Merchant'
import { MerchantPayment, merchantPaymentSchema } from '@src/data/types/MerchantPayment'
import { EditPersonalLoanWorksheetDto, PersonalLoanWorksheet } from '@src/data/types/PersonalWorksheetSchema'
import { sumBy } from 'lodash-es'
import { useMemo } from 'react'

export interface PersonalEditValues extends BaseWorksheetFormValues {
  merchantPayments: MerchantPayment[]
  highEstimateExplainedToClient: boolean
}

export type PersonalEditSchema = ReturnType<typeof usePersonalEditSchema>

export type PersonalEditSubmitValues = yup.InferSubmit<PersonalEditSchema>

// Base worksheet fields come from the shared schema (amount caps, plan/term availability via the
// TermPlanSelect validation context, activation past/expiry). Only the page's extra fields are
// declared here; cross-payment rules stay submit-time business checks like the legacy page.
export const usePersonalEditSchema = (config: BaseWorksheetFormConfig) => {
  const fields = useBaseWorksheetFormFields(config)

  return useMemo(
    () =>
      yup.object({
        amountRequested: fields.amountRequested,
        paymentPlanId: fields.paymentPlanId,
        term: fields.term,
        paymentFrequency: fields.paymentFrequency,
        activationDate: fields.activationDate,
        firstPaymentDate: fields.firstPaymentDate,
        includeProtection: fields.includeProtection,
        isHighEstimate: fields.isHighEstimate,
        // Composed rather than `mixed` so each row's amount and method validate on their own
        // field; the cross-row total is a submit-time business rule.
        merchantPayments: yup.array().of(merchantPaymentSchema).required().default([]),
        highEstimateExplainedToClient: yup.boolean().required().default(false),
      }),
    [fields],
  )
}

// Seeds the form from the stored worksheet on load/reset. Pure so it can be unit-tested without
// mounting the form; `defaults` is the schema's own `getDefault()` output.
export const fromWorksheet = (
  defaults: PersonalEditValues,
  worksheet: PersonalLoanWorksheet | null,
): PersonalEditValues => {
  const base = buildBaseWorksheetFormInitialValues(defaults, (worksheet as BaseWorksheetDto | null) ?? null, null)
  return {
    ...base,
    merchantPayments: worksheet?.merchantPayments ?? [],
    highEstimateExplainedToClient: worksheet?.highEstimateExplainedToClient ?? false,
  }
}

export const usePersonalEditForm = (config: BaseWorksheetFormConfig, worksheet: PersonalLoanWorksheet | null) => {
  const schema = usePersonalEditSchema(config)
  const isReady = useBaseWorksheetFormReady(config)

  const defaultValues = useMemo<PersonalEditValues | undefined>(() => {
    if (!isReady) return undefined
    return fromWorksheet(schema.getDefault() as PersonalEditValues, worksheet)
  }, [isReady, schema, worksheet])

  return { schema, defaultValues, isReady }
}

// A saved high-estimate worksheet with a CVT starts locked: every field disabled until the agent
// explicitly unlocks (which cancels the contract).
export const getInitialHighEstimateLock = (
  worksheet: PersonalLoanWorksheet | null | undefined,
  merchant: Merchant | null | undefined,
  hasCVT: boolean,
): boolean => !!worksheet?.isHighEstimate && merchant?.canAcceptHighEstimate === true && hasCVT

// The saved selection, so TermPlanSelect can keep it visible as a disabled row when the server
// no longer offers the pair.
export const getStoredTermAndPlan = (worksheet: PersonalLoanWorksheet | null | undefined): StoredTermAndPlan | null =>
  worksheet?.paymentPlanId && worksheet.term
    ? { paymentPlanId: worksheet.paymentPlanId, term: worksheet.term }
    : null

export interface ValidatePersonalSubmitInput {
  values: PersonalEditSubmitValues
  merchant: Merchant
  creditApplication: CreditApplication
  availableCredit: number
}

// The rules that span rows or reach outside the form: the merchant must cover its own promo fees,
// the payments must add up to the financed amount, the amount must fit the available credit, and
// the activation date must stay within 90 days of the credit report.
export const validatePersonalSubmit = ({
  values,
  merchant,
  creditApplication,
  availableCredit,
}: ValidatePersonalSubmitInput): WorksheetError[] => {
  const errors: WorksheetError[] = []

  const selectedPlan = merchant.paymentPlans?.find((p) => (p.id as string) === values.paymentPlanId)
  if (isPromoPlan(values.paymentPlanId) && selectedPlan) {
    const merchantFee = (selectedPlan.merchantFeeRate / 100) * values.amountRequested
    const mainPayment = values.merchantPayments.find((p) => p.merchantId === merchant.id)
    if (!mainPayment || merchantFee > mainPayment.amount) {
      errors.push({
        code: 'merchantMustCoverFees',
        messageKey: 'worksheetCommon.merchantMustHaveFees',
        messageArgs: { merchantFee },
      })
    }
  }

  if (values.amountRequested > availableCredit) {
    errors.push({
      code: 'amountOverAvailableCredit',
      messageKey: 'worksheetCommon.amountRequestedHigherThanAvailableCredit',
    })
  }

  if (sumBy(values.merchantPayments, 'amount') !== values.amountRequested) {
    errors.push({
      code: 'paymentTotalMismatch',
      messageKey: 'worksheetCommon.merchantPaymentTotalMismatch',
    })
  }

  const hardHitReport = creditApplication.applicant.hardHitReport ?? creditApplication.coApplicant?.hardHitReport
  if (hardHitReport?.receivedOn) {
    const latestActivation = new DateOnly(hardHitReport.receivedOn)
    latestActivation.setDate(latestActivation.getDate() + 90)
    if (values.activationDate > new DateOnly(latestActivation)) {
      errors.push({
        code: 'activationAfterReport90Days',
        messageKey: 'worksheetCommon.activationDateReport90Days',
      })
    }
  }

  return errors
}

export const toEditPersonalLoanWorksheetDto = (
  values: PersonalEditSubmitValues,
  worksheet: PersonalLoanWorksheet,
): EditPersonalLoanWorksheetDto => ({
  status: EWorksheetStatus.Active,
  amountRequested: values.amountRequested,
  deliveryOn: values.activationDate.toString(),
  firstPaymentOn: values.firstPaymentDate?.toString() ?? '',
  includeInsurance: values.includeProtection,
  isHighEstimate: values.isHighEstimate,
  highEstimateExplainedToClient: values.highEstimateExplainedToClient,
  paymentFrequency: values.paymentFrequency,
  term: values.term ?? 0,
  merchantPayments: values.merchantPayments,
  paymentPlanId: values.paymentPlanId,
  creditApplicationId: worksheet.creditApplicationId,
  versionTag: worksheet.versionTag,
})
